import { Host, forceUpdate, h } from "@stencil/core";
import { adoptCommonThemes } from "../../common/theme";
import { getControlRegisterProperty, registryControlProperty } from "../../common/registry-properties";
import { NAVIGATION_LIST_INITIAL_LEVEL } from "./utils";
import { fromGxImageToURL } from "../tree-view/genexus-implementation";
import { SCROLLABLE_CLASS } from "../../common/reserved-names";
import { removeSubscription, subscribe, syncStateWithObservableAncestors } from "../sidebar/expanded-change-obervables";
import { formatImagePath } from "../../common/utils";
// - - - - - - - - - - - - - - - - - - - -
//                Registry
// - - - - - - - - - - - - - - - - - - - -
// This callback will be registered by default. If it is used in GeneXus, all
// tree views will have the same state, so the parameters used of the treeState
// are "shared" across all tree view instances
// TODO: For some reason, this module import is different when an external
// library imports the registryControlProperty function. We should de-dup this
// to fix issues related with double initialization of the registry
const registerDefaultGetImagePathCallback = (navigationListState) => registryControlProperty("getImagePathCallback", "ch-navigation-list-render", (item) => ({
    base: formatImagePath(navigationListState.useGxRender
        ? fromGxImageToURL(item.startImgSrc, navigationListState.gxSettings, navigationListState.gxImageConstructor)
        : item.startImgSrc, item.startImgType)
}));
const isSelectedLink = (item, navigationListState) => !!item.link &&
    !!navigationListState.selectedLink?.link?.url &&
    navigationListState.selectedLink.link.url === item.link.url &&
    navigationListState.selectedLink.id === item.id;
// items != null comparison is based on the following benchmark
// https://www.measurethat.net/Benchmarks/Show/6389/0/compare-comparison-with-null-or-undefined
const defaultRender = (item, navigationListState, level, index) => (h("ch-navigation-list-item", { key: item.id ?? `${level}-${index}`, id: item.id, caption: item.caption, disabled: item.disabled, expandable: item.items != null, expandableButton: navigationListState.expandableButton, expandableButtonPosition: navigationListState.expandableButtonPosition, expanded: item.expanded, level: level, link: item.link, model: item, navigationListExpanded: navigationListState.expanded, selected: isSelectedLink(item, navigationListState), selectedLinkIndicator: navigationListState.selectedLinkIndicator, showCaptionOnCollapse: navigationListState.showCaptionOnCollapse, startImgSrc: item.startImgSrc, startImgType: item.startImgType, tooltipDelay: navigationListState.tooltipDelay }, navigationListState.expanded &&
    item.items != null &&
    item.items.map((item, subItemIndex) => navigationListState.renderItem(item, navigationListState, level + 1, subItemIndex))));
let autoId = 0;
const NAVIGATION_LIST_ITEM = "ch-navigation-list-item";
/**
 * @status experimental
 */
export class ChNavigationListRender {
    constructor() {
        this.autoGrow = false;
        this.expandableButton = "decorative";
        this.expandableButtonPosition = "start";
        this.getImagePathCallback = undefined;
        this.expanded = true;
        this.expandSelectedLink = false;
        this.gxImageConstructor = undefined;
        this.gxSettings = undefined;
        this.model = undefined;
        this.renderItem = defaultRender;
        this.selectedLink = {
            link: { url: undefined }
        };
        this.selectedLinkIndicator = false;
        this.showCaptionOnCollapse = "inline";
        this.tooltipDelay = 100;
        this.useGxRender = false;
    }
    /**
     * This ID is used to identify the Navigation List. Necessary to subscribe
     * for expand/collapse changes in the ancestor nodes.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #navigationListId;
    el;
    modelChanged() {
        if (this.model && this.expandSelectedLink) {
            this.#expandNewSelectedLink(this.model);
        }
    }
    selectedLinkChanged() {
        if (this.model && this.expandSelectedLink) {
            this.#expandNewSelectedLink(this.model);
        }
    }
    /**
     * Fired when an button is clicked.
     * This event can be prevented.
     */
    buttonClick;
    /**
     * Fired when an hyperlink is clicked.
     * This event can be prevented.
     */
    hyperlinkClick;
    #expandNewSelectedLink = (model) => {
        // for let index ... is the fastest for
        for (let index = 0; index < model.length; index++) {
            const itemUIModel = model[index];
            if (isSelectedLink(itemUIModel, this)) {
                itemUIModel.expanded = true;
                return true;
            }
            if (itemUIModel.items != null) {
                const selectedLinkIsChild = this.#expandNewSelectedLink(itemUIModel.items);
                if (selectedLinkIsChild) {
                    itemUIModel.expanded = true;
                    return true;
                }
            }
        }
        return false;
    };
    #handleItemClick = (event) => {
        const composedPath = event.composedPath();
        const itemActionIndex = composedPath.findIndex(el => el.tagName?.toLowerCase() === "button" ||
            el.tagName?.toLowerCase() === "a");
        if (itemActionIndex === -1) {
            return;
        }
        const navigationListItem = composedPath[itemActionIndex + 2];
        // Get the navigation list item of the event
        if (!navigationListItem ||
            navigationListItem.tagName?.toLowerCase() !== NAVIGATION_LIST_ITEM) {
            return;
        }
        const itemUIModel = navigationListItem.model;
        const canExpandSubItems = this.expanded;
        if (itemUIModel.link) {
            const eventInfo = this.hyperlinkClick.emit({ event, item: itemUIModel });
            if (eventInfo.defaultPrevented) {
                event.preventDefault();
                return;
            }
            // Update the selected link
            this.selectedLink = { id: itemUIModel.id, link: itemUIModel.link };
        }
        else {
            const eventInfo = this.buttonClick.emit(itemUIModel);
            if (eventInfo.defaultPrevented) {
                event.preventDefault();
                return;
            }
        }
        // TODO: Add an unit test for this
        if (canExpandSubItems && itemUIModel.items != null) {
            itemUIModel.expanded = !itemUIModel.expanded;
            forceUpdate(this);
        }
    };
    connectedCallback() {
        this.#navigationListId ??= `ch-navigation-list-render-${autoId++}`;
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        // If the getImagePathCallback was not previously registered
        if (!getControlRegisterProperty("getImagePathCallback", "ch-navigation-list-render")) {
            registerDefaultGetImagePathCallback(this);
        }
        if (this.model && this.expandSelectedLink) {
            this.#expandNewSelectedLink(this.model);
        }
        // Subscribe to expand/collapse changes in the ancestor nodes
        subscribe(this.#navigationListId, {
            getSubscriberRef: () => this.el,
            observerCallback: expanded => {
                this.expanded = expanded;
            }
        });
        // Initialize the state
        syncStateWithObservableAncestors(this.#navigationListId);
        // Static attributes that we including in the Host functional component to
        // eliminate additional overhead
        this.el.setAttribute("role", "list");
    }
    disconnectedCallback() {
        removeSubscription(this.#navigationListId);
    }
    render() {
        return (h(Host, { key: '4226aca7daacd32120ad6b27fa34af23eeb8072e', class: {
                [SCROLLABLE_CLASS]: true,
                "ch-navigation-list--collapsed": !this.expanded,
                "ch-navigation-list--contain": !this.autoGrow
            }, onClick: this.#handleItemClick }, this.model?.map((item, index) => this.renderItem(item, this, NAVIGATION_LIST_INITIAL_LEVEL, index))));
    }
    static get is() { return "ch-navigation-list-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["navigation-list-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["navigation-list-render.css"]
        };
    }
    static get properties() {
        return {
            "autoGrow": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `false` the overflowing content of the control will be clipped to the\nborders of its container."
                },
                "attribute": "auto-grow",
                "reflect": false,
                "defaultValue": "false"
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"decorative\" | \"no\"",
                    "resolved": "\"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed in the items by\ndefault.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandableButtonPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"start\" | \"end\"",
                    "resolved": "\"end\" | \"start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the expandable button in reference of the action\nelement of the items\n - `\"start\"`: Expandable button is placed before the action element.\n - `\"end\"`: Expandable button is placed after the action element."
                },
                "attribute": "expandable-button-position",
                "reflect": false,
                "defaultValue": "\"start\""
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    item: NavigationListItemModel\n  ) => GxImageMultiState | undefined",
                    "resolved": "(item: NavigationListItemModel) => GxImageMultiState",
                    "references": {
                        "NavigationListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/navigation-list/types.ts::NavigationListItemModel"
                        },
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "expanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the control is expanded or collapsed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "true"
            },
            "expandSelectedLink": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to expand the path to the selected link when the `selectedLink`\nproperty is updated."
                },
                "attribute": "expand-selected-link",
                "reflect": false,
                "defaultValue": "false"
            },
            "gxImageConstructor": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(name: string) => any",
                    "resolved": "(name: string) => any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                }
            },
            "gxSettings": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "gx-settings",
                "reflect": false
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "NavigationListModel | undefined",
                    "resolved": "NavigationListItemModel[]",
                    "references": {
                        "NavigationListModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/navigation-list/types.ts::NavigationListModel"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the control."
                }
            },
            "renderItem": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    item: NavigationListItemModel,\n    navigationListState: ChNavigationListRender,\n    level: number,\n    index: number\n  ) => any",
                    "resolved": "(item: NavigationListItemModel, navigationListState: ChNavigationListRender, level: number, index: number) => any",
                    "references": {
                        "NavigationListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/navigation-list/types.ts::NavigationListItemModel"
                        },
                        "ChNavigationListRender": {
                            "location": "global",
                            "id": "global::ChNavigationListRender"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the control."
                },
                "defaultValue": "defaultRender"
            },
            "selectedLink": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "{ id?: string; link: ItemLink }",
                    "resolved": "{ id?: string; link: ItemLink; }",
                    "references": {
                        "ItemLink": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::ItemLink"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the current selected hyperlink."
                },
                "defaultValue": "{\n    link: { url: undefined }\n  }"
            },
            "selectedLinkIndicator": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the selected item indicator is displayed (only work for hyperlink)"
                },
                "attribute": "selected-link-indicator",
                "reflect": false,
                "defaultValue": "false"
            },
            "showCaptionOnCollapse": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"inline\" | \"tooltip\"",
                    "resolved": "\"inline\" | \"tooltip\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the caption of the items will be displayed when the control\nis collapsed"
                },
                "attribute": "show-caption-on-collapse",
                "reflect": false,
                "defaultValue": "\"inline\""
            },
            "tooltipDelay": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the delay (in ms) for the tooltip to be displayed."
                },
                "attribute": "tooltip-delay",
                "reflect": false,
                "defaultValue": "100"
            },
            "useGxRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Navigation List as a UC 2.0 in\nGeneXus."
                },
                "attribute": "use-gx-render",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "buttonClick",
                "name": "buttonClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an button is clicked.\nThis event can be prevented."
                },
                "complexType": {
                    "original": "NavigationListItemModel",
                    "resolved": "{ id?: string; caption: string; disabled?: boolean; expanded?: boolean; metadata?: string; startImgSrc?: string; startImgType?: \"mask\" | \"background\"; link?: ItemLink; items?: NavigationListModel; }",
                    "references": {
                        "NavigationListItemModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/navigation-list/types.ts::NavigationListItemModel"
                        }
                    }
                }
            }, {
                "method": "hyperlinkClick",
                "name": "hyperlinkClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an hyperlink is clicked.\nThis event can be prevented."
                },
                "complexType": {
                    "original": "NavigationListHyperlinkClickEvent",
                    "resolved": "{ event: PointerEvent; item: NavigationListItemModel; }",
                    "references": {
                        "NavigationListHyperlinkClickEvent": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/navigation-list/types.ts::NavigationListHyperlinkClickEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "selectedLink",
                "methodName": "selectedLinkChanged"
            }];
    }
}
